import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Bell, BellOff, Check, X, ChefHat, Bike, PackageCheck, Phone, MapPin, Clock,
    Loader2, Volume2, VolumeX, LogOut, BarChart3, ShoppingBag, ListChecks,
    Flame, Radio, Sparkles, Activity, RefreshCw, Search
} from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STATUS_FLOW = ["pending", "accepted", "preparing", "ready", "on_the_way", "completed"];
const STATUS_META = {
    pending:    { label: "NEW",         color: "from-amber-400 to-orange-500", next: "accepted",   icon: Bell,         action: "ACCEPT" },
    accepted:   { label: "ACCEPTED",    color: "from-cyan-400 to-blue-500",    next: "preparing",  icon: Check,        action: "START PREP" },
    preparing:  { label: "PREPARING",   color: "from-orange-400 to-red-500",   next: "ready",      icon: ChefHat,      action: "READY" },
    ready:      { label: "READY",       color: "from-emerald-400 to-green-500",next: "on_the_way", icon: PackageCheck, action: "DISPATCH" },
    on_the_way: { label: "ON THE WAY",  color: "from-sky-400 to-indigo-500",   next: "completed",  icon: Bike,         action: "DELIVERED" },
    completed:  { label: "COMPLETED",   color: "from-zinc-400 to-zinc-500",    next: null,         icon: PackageCheck, action: null },
    rejected:   { label: "REJECTED",    color: "from-rose-400 to-rose-600",    next: null,         icon: X,            action: null },
    cancelled:  { label: "CANCELLED",   color: "from-zinc-400 to-zinc-500",    next: null,         icon: X,            action: null },
};

// === Web Audio "ding" — no asset dependency ===
const playDing = () => {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const tones = [880, 1175, 1568];
        tones.forEach((freq, i) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.frequency.value = freq; o.type = "sine";
            const t0 = ctx.currentTime + i * 0.13;
            g.gain.setValueAtTime(0, t0);
            g.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
            g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.4);
            o.start(t0); o.stop(t0 + 0.45);
        });
        setTimeout(() => ctx.close().catch(() => {}), 800);
    } catch {}
};

const fmtAge = (iso) => {
    if (!iso) return "—";
    try {
        const t = new Date(iso).getTime();
        const m = Math.floor((Date.now() - t) / 60000);
        if (m < 1) return "now";
        if (m < 60) return `${m}'`;
        return `${Math.floor(m / 60)}h`;
    } catch { return "—"; }
};

export const AdminCommand = () => {
    const navigate = useNavigate();
    const { user, loading: authLoading, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [soundOn, setSoundOn] = useState(() => localStorage.getItem("admin_sound") !== "0");
    const [filter, setFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [pulseId, setPulseId] = useState(null);
    const [wsState, setWsState] = useState("connecting");  // connecting | live | reconnecting
    const wsRef = useRef(null);
    const knownIds = useRef(new Set());

    // ---- Auth gate ----
    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/admin/login"); return; }
        if (user.role !== "admin") { navigate("/admin/login"); return; }
        // ask for browser notification permission once
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().catch(() => {});
        }
        fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading]);

    // ---- Live WS ----
    useEffect(() => {
        if (authLoading || !user || user.role !== "admin") return;
        const token = localStorage.getItem("sh_token");
        if (!token) return;
        const url = `${API.replace("https://", "wss://").replace("http://", "ws://")}/ws/admin?token=${encodeURIComponent(token)}`;
        let stopped = false;
        let backoff = 1500;

        const connect = () => {
            if (stopped) return;
            try {
                const ws = new WebSocket(url);
                wsRef.current = ws;
                ws.onopen  = () => setWsState("live");
                ws.onclose = () => {
                    if (stopped) return;
                    setWsState("reconnecting");
                    setTimeout(connect, backoff);
                    backoff = Math.min(backoff * 1.5, 8000);
                };
                ws.onerror = () => {};
                ws.onmessage = (msg) => {
                    try {
                        const data = JSON.parse(msg.data);
                        if (data.type === "new_order" && data.order) {
                            handleNewOrder(data.order);
                        } else if (data.type === "order_updated" && data.order) {
                            handleOrderUpdated(data.order);
                        }
                    } catch {}
                };
            } catch {
                setTimeout(connect, backoff);
            }
        };
        connect();
        return () => { stopped = true; try { wsRef.current?.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/orders");
            setOrders(data || []);
            (data || []).forEach((o) => knownIds.current.add(o.id));
        } catch {
            toast.error("Σφάλμα φόρτωσης παραγγελιών");
        } finally {
            setLoading(false);
        }
    };

    const handleNewOrder = (order) => {
        if (knownIds.current.has(order.id)) return;
        knownIds.current.add(order.id);
        setOrders((prev) => [order, ...prev]);
        setPulseId(order.id);
        setTimeout(() => setPulseId(null), 6000);
        if (soundOn) playDing();
        toast.success(`🔔 Νέα παραγγελία · €${order.total?.toFixed(2)}`, { duration: 4000 });
        try {
            if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Νέα παραγγελία · Grill Bros", {
                    body: `${order.customer_name} · €${order.total?.toFixed(2)}`,
                    icon: "/favicon.ico",
                });
            }
        } catch {}
    };

    const handleOrderUpdated = (order) => {
        setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...order } : o)));
    };

    const updateStatus = async (orderId, status) => {
        try {
            const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status });
            handleOrderUpdated(data);
            toast.success(`→ ${STATUS_META[status]?.label || status}`, { duration: 1500 });
        } catch (err) {
            toast.error("Αποτυχία ενημέρωσης");
        }
    };

    const toggleSound = () => {
        const v = !soundOn;
        setSoundOn(v);
        localStorage.setItem("admin_sound", v ? "1" : "0");
    };

    // ---- Derived ----
    const filtered = useMemo(() => {
        let out = orders;
        if (filter === "active")  out = out.filter((o) => !["completed","delivered","rejected","cancelled"].includes(o.status));
        if (filter === "history") out = out.filter((o) =>  ["completed","delivered","rejected","cancelled"].includes(o.status));
        if (search.trim()) {
            const q = search.toLowerCase();
            out = out.filter((o) =>
                o.id.toLowerCase().includes(q) ||
                (o.customer_name || "").toLowerCase().includes(q) ||
                (o.phone || "").includes(q)
            );
        }
        return out;
    }, [orders, filter, search]);

    const kpis = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const todays = orders.filter((o) => (o.created_at || "").startsWith(today) && !["rejected","cancelled"].includes(o.status));
        const active = orders.filter((o) => !["completed","delivered","rejected","cancelled"].includes(o.status));
        return {
            today_revenue: todays.reduce((s, o) => s + (o.total || 0), 0),
            today_orders:  todays.length,
            active:        active.length,
            new:           orders.filter((o) => o.status === "pending").length,
        };
    }, [orders]);

    if (authLoading || loading) {
        return (
            <div className="burger-bg min-h-screen grid place-items-center">
                <Loader2 className="animate-spin text-[var(--burger-orange-2)]" size={36} />
            </div>
        );
    }

    return (
        <div data-testid="admin-command" className="burger-bg min-h-screen text-white">
            {/* HEADER */}
            <header className="sticky top-0 z-30 cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 py-3 flex items-center gap-3">
                <span className="h-10 w-10 grid place-items-center rounded-xl ember-gradient shrink-0">
                    <Radio size={16} className="text-black" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="font-display text-[10px] tracking-[0.32em] text-white/45">// COMMAND CENTER</p>
                    <h1 className="font-display text-xl tracking-[0.18em]">GRILL BROS · OPS</h1>
                </div>
                <span
                    data-testid="ws-status"
                    className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10px] font-mono uppercase tracking-widest ${
                        wsState === "live" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" :
                        wsState === "reconnecting" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" :
                        "bg-white/10 text-white/55 border border-white/15"
                    }`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full ${wsState === "live" ? "bg-emerald-400 animate-pulse" : "bg-white/45"}`} />
                    {wsState}
                </span>
                <button onClick={toggleSound} data-testid="sound-toggle" className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-white/10">
                    {soundOn ? <Volume2 size={15} className="text-[var(--burger-orange-2)]" /> : <VolumeX size={15} className="text-white/40" />}
                </button>
                <button onClick={fetchOrders} data-testid="refresh-btn" className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-white/10">
                    <RefreshCw size={15} />
                </button>
                <button onClick={() => navigate("/admin/menu")} data-testid="goto-menu" className="hidden sm:grid h-10 w-10 place-items-center rounded-xl bg-white/5 hover:bg-white/10" title="Menu CMS">
                    <ListChecks size={15} />
                </button>
                <button onClick={() => navigate("/admin/analytics")} data-testid="goto-analytics" className="hidden sm:grid h-10 w-10 place-items-center rounded-xl bg-white/5 hover:bg-white/10" title="Analytics">
                    <BarChart3 size={15} />
                </button>
                <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-rose-500/20">
                    <LogOut size={15} />
                </button>
            </header>

            {/* KPI STRIP */}
            <section data-testid="kpi-strip" className="px-5 mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <KPI label="NEW" value={kpis.new} icon={Bell} pulse={kpis.new > 0} testid="kpi-new" />
                <KPI label="ACTIVE" value={kpis.active} icon={Activity} testid="kpi-active" />
                <KPI label="TODAY" value={kpis.today_orders} icon={ShoppingBag} testid="kpi-today" />
                <KPI label="REVENUE" value={`€${kpis.today_revenue.toFixed(2)}`} icon={Sparkles} testid="kpi-revenue" />
            </section>

            {/* FILTER + SEARCH */}
            <section className="px-5 mt-5 flex items-center gap-2">
                <div className="flex gap-1 p-1 rounded-full glass-burger">
                    {[
                        { id: "active",  label: "ACTIVE",  c: orders.filter((o) => !["completed","delivered","rejected","cancelled"].includes(o.status)).length },
                        { id: "history", label: "HISTORY", c: orders.filter((o) =>  ["completed","delivered","rejected","cancelled"].includes(o.status)).length },
                    ].map((t) => {
                        const on = filter === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`filter-${t.id}`}
                                onClick={() => setFilter(t.id)}
                                className={`relative px-4 h-9 rounded-full font-display text-xs tracking-widest transition ${
                                    on ? "text-black" : "text-white/70 hover:text-white"
                                }`}
                            >
                                {on && <motion.span layoutId="adm-pill" className="absolute inset-0 -z-10 rounded-full ember-gradient" />}
                                {t.label} <span className={on ? "text-black/55" : "text-white/40"}>· {t.c}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-full glass-burger">
                    <Search size={13} className="text-white/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Order, name, phone..."
                        data-testid="search-input"
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
                    />
                </div>
            </section>

            {/* ORDER GRID */}
            <section data-testid="orders-grid" className="px-5 mt-5 pb-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence>
                    {filtered.length === 0 && (
                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="sm:col-span-2 lg:col-span-3 text-center py-16 text-white/40 text-sm"
                        >
                            Καμία παραγγελία ακόμα.
                        </motion.p>
                    )}
                    {filtered.map((o) => (
                        <OrderCard
                            key={o.id}
                            order={o}
                            pulsing={pulseId === o.id}
                            onAdvance={(next) => updateStatus(o.id, next)}
                            onReject={() => updateStatus(o.id, "rejected")}
                        />
                    ))}
                </AnimatePresence>
            </section>
        </div>
    );
};

const KPI = ({ label, value, icon: Icon, pulse, testid }) => (
    <motion.div
        data-testid={testid}
        whileHover={{ y: -2 }}
        className="glass-burger rounded-2xl p-3 border border-white/10 relative overflow-hidden"
    >
        {pulse && <motion.span animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }} transition={{ duration: 1.8, repeat: Infinity }} className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[var(--burger-orange)]" />}
        <div className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.3em] text-white/45">
            <Icon size={11} className="text-[var(--burger-orange-2)]" />
            {label}
        </div>
        <p className="font-display text-3xl mt-1">{value}</p>
    </motion.div>
);

const OrderCard = ({ order, pulsing, onAdvance, onReject }) => {
    const meta = STATUS_META[order.status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            data-testid={`order-${order.id}`}
            className={`glass-burger rounded-2xl p-4 border ${pulsing ? "border-[var(--burger-orange)] shadow-[0_0_30px_rgba(255,138,0,0.5)]" : "border-white/8"} relative overflow-hidden`}
        >
            {pulsing && (
                <motion.div
                    initial={{ scale: 0.8, opacity: 0.4 }}
                    animate={{ scale: 1.4, opacity: 0 }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    className="absolute inset-0 -z-10 rounded-2xl border-2 border-[var(--burger-orange)]"
                />
            )}
            <div className="flex items-center justify-between">
                <div>
                    <p className="font-mono text-[10px] text-white/45 tracking-wider">#{order.id.slice(0,8).toUpperCase()}</p>
                    <p className="font-display text-lg tracking-wide mt-0.5">{order.customer_name?.toUpperCase() || "—"}</p>
                </div>
                <div className={`px-2 h-7 grid place-items-center rounded-full bg-gradient-to-br ${meta.color} text-black flex items-center gap-1.5`}>
                    <Icon size={12} strokeWidth={3} />
                    <span className="font-display text-[10px] tracking-widest">{meta.label}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-white/65">
                <span className="flex items-center gap-1"><Clock size={11} /> {fmtAge(order.created_at)}</span>
                {order.phone && <a href={`tel:${order.phone}`} className="flex items-center gap-1 hover:text-[var(--burger-orange-2)]"><Phone size={11} /> {order.phone}</a>}
                {order.address && <span className="flex items-center gap-1 truncate max-w-[180px]"><MapPin size={11} /> {order.address}</span>}
            </div>

            <ul className="mt-3 space-y-1 max-h-28 overflow-y-auto cinematic-scrollbar pr-1">
                {(order.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between text-xs text-white/80">
                        <span className="truncate pr-2"><span className="text-[var(--burger-orange-2)]">{it.quantity}×</span> {it.name}</span>
                        <span className="font-mono shrink-0">€{(it.price * it.quantity).toFixed(2)}</span>
                    </li>
                ))}
            </ul>

            {order.notes && (
                <p className="mt-2 text-[11px] italic text-[var(--burger-orange-2)] border-l-2 border-[var(--burger-orange)]/40 pl-2">"{order.notes}"</p>
            )}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                <span className="font-display text-xl ember-text">€{order.total?.toFixed(2)}</span>
                <div className="flex gap-2">
                    {order.status === "pending" && (
                        <button
                            onClick={onReject}
                            data-testid={`reject-${order.id}`}
                            className="h-10 px-3 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 font-display tracking-widest text-[10px]"
                        >
                            REJECT
                        </button>
                    )}
                    {meta.next && (
                        <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => onAdvance(meta.next)}
                            data-testid={`advance-${order.id}`}
                            className="magnet-cta primary h-10 px-4 text-[10px]"
                        >
                            {meta.action}
                        </motion.button>
                    )}
                </div>
            </div>
        </motion.article>
    );
};

export default AdminCommand;
